import { useEffect, useState } from 'react';
import { getDayOverview } from '../hooks/useDayOverview';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const toIsoDate = (date) => date.toISOString().split('T')[0];

const computePreviousRange = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const lengthDays = Math.round((end - start) / MS_PER_DAY) + 1;
    const prevEnd = new Date(start.getTime() - MS_PER_DAY);
    const prevStart = new Date(prevEnd.getTime() - (lengthDays - 1) * MS_PER_DAY);
    return { prevStart: toIsoDate(prevStart), prevEnd: toIsoDate(prevEnd) };
};

const aggregate = (data) => {
    if (!data) return { passed: 0, failed: 0, skipped: 0 };
    return Object.values(data).reduce(
        (acc, { passed, failed, skipped }) => ({
            passed: acc.passed + passed,
            failed: acc.failed + failed,
            skipped: acc.skipped + skipped,
        }),
        { passed: 0, failed: 0, skipped: 0 }
    );
};

const useFiltersData = (selectedStartDate, selectedEndDate, selectedEnvironment, selectedPackage, selectedFramework, selectedUnit, isCsqXp = null) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);

            try {
                const { prevStart, prevEnd } = computePreviousRange(selectedStartDate, selectedEndDate);
                const [current, previous] = await Promise.all([
                    getDayOverview(selectedStartDate, selectedEndDate, selectedEnvironment, selectedPackage, selectedFramework, selectedUnit, isCsqXp),
                    getDayOverview(prevStart, prevEnd, selectedEnvironment, selectedPackage, selectedFramework, selectedUnit, isCsqXp),
                ]);
                setData({ current, previous });
            } catch (err) {
                setError(err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [selectedStartDate, selectedEndDate, selectedEnvironment, selectedPackage, selectedFramework, selectedUnit, isCsqXp]);

    const processedData = data
      ? (() => {
          const current = aggregate(data.current);
          const previous = aggregate(data.previous);

          if (!Object.keys(data.current || {}).length) return null;

          const previousTotal = previous.passed + previous.failed;

          return {
            passed: current.passed,
            failed: current.failed,
            skipped: current.skipped,
            lastWeekSuccessRate:
              previousTotal > 0 ? (previous.passed / previousTotal) * 100 : null,
          };
        })()
      : null;

    return { data: processedData, rawData: data?.current ?? null, loading, error };
};

export default useFiltersData;
